/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type {
	EntryPoint,
	EntryPointComponent,
	JSResourceReference,
	PreloadProps,
} from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type { OperationType } from 'relay-runtime';
import type { NullableEntryPoint } from '@atlassian/react-entrypoint';
import { spaEntryPoint } from '@jira/platform__page-container-v2/src/spaEntryPoint.entrypoint.tsx';
import {
	PREVIEW_PANEL,
	HORIZONTAL_NAVIGATION,
	SPA_CONTENT,
	MAIN_CONTENT,
	TOP_NAVIGATION,
	RIGHT_SIDEBAR,
	LEFT_SIDEBAR,
	REDIRECT,
} from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { AnyEntryPoint } from '@jira/platform__entry-point/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { topNavEntryPoint } from '@jira/navigation-apps__atlassian-navigation/src/topNavEntryPoint.entrypoint.tsx';
import type { JiraSpaPageContainerQuery } from '@jira/platform__page-container-v2/src/common/ui/page-container/__generated__/JiraSpaPageContainerQuery.graphql';
import type { JiraSpaPageContainerEntryEntrypointProps } from '@jira/platform__page-container-v2/src/common/ui/page-container/JiraSpaPageContainerEntry.entrypoint.types.tsx';
import type { RouterContext } from '@atlassian/react-resource-router';

type ChromelessPage = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	main: NullableEntryPoint<EntryPointComponent<any, any, any, any>, EntryPointRouteParams>;
};

type Page = ChromelessPage & {
	previewPanel?: AnyEntryPoint;
	horizontalNavigation?: AnyEntryPoint;
	horizontalNavigationFallback?: () => JSX.Element;
	leftSidebar?: {
		sidebar: AnyEntryPoint;
		entryPoints?: Record<string, { entryPoint: AnyEntryPoint }>;
		isAppsExpanded?: boolean;
		isFiltersExpanded?: boolean;
		isPlansExpanded?: boolean;
		isProjectsExpanded?: boolean;
		isOperationsExpanded?: boolean;
	};
	rightSidebar?: AnyEntryPoint;
	rightSidebarFallback?: () => JSX.Element;
	redirect?: AnyEntryPoint;
};

export const createChromelessEntryPoint = (
	page: ChromelessPage,
): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutSpaContent?:
				| {
						entryPoint: EntryPoint<
							EntryPointComponent<
								{
									queryReference: JiraSpaPageContainerQuery;
								},
								{},
								JiraSpaPageContainerEntryEntrypointProps,
								{}
							>,
							EntryPointRouteParams
						>;
						entryPointParams: EntryPointRouteParams;
						options: {
							fetchPolicy: 'store-or-network';
						};
				  }
				| undefined;
		};
	};
	toString: () => string;
} => ({
	root: page.main.root,
	getPreloadProps: (
		entryPointParams: EntryPointRouteParams,
	): {
		entryPoints: {
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutSpaContent?:
				| {
						entryPoint: EntryPoint<
							EntryPointComponent<
								{
									queryReference: JiraSpaPageContainerQuery;
								},
								{},
								JiraSpaPageContainerEntryEntrypointProps,
								{}
							>,
							EntryPointRouteParams
						>;
						entryPointParams: EntryPointRouteParams;
						options: {
							fetchPolicy: 'store-or-network';
						};
				  }
				| undefined;
		};
	} => ({
		entryPoints: {
			...(fg('jpx-463-spa-relay-query')
				? {
						[SPA_CONTENT]: {
							entryPoint: spaEntryPoint,
							entryPointParams,
							options: {
								fetchPolicy: 'store-or-network' as const,
							},
						},
					}
				: undefined),
			[MAIN_CONTENT]: {
				entryPoint: page.main,
				entryPointParams,
			},
		},
	}),
	toString: () => 'ChromelessEntryPoint',
});

export const createPageEntryPoint = (
	page: Page,
): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutSpaContent?:
				| {
						entryPoint: EntryPoint<
							EntryPointComponent<
								{
									queryReference: JiraSpaPageContainerQuery;
								},
								{},
								JiraSpaPageContainerEntryEntrypointProps,
								{}
							>,
							EntryPointRouteParams
						>;
						entryPointParams: EntryPointRouteParams;
						options: {
							fetchPolicy: 'store-or-network';
						};
				  }
				| undefined;
		};
	};
	toString: () => string;
} => ({
	root: page.main.root,
	getPreloadProps: (
		entryPointParams: EntryPointRouteParams,
	): {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutSpaContent?:
				| {
						entryPoint: EntryPoint<
							EntryPointComponent<
								{
									queryReference: JiraSpaPageContainerQuery;
								},
								{},
								JiraSpaPageContainerEntryEntrypointProps,
								{}
							>,
							EntryPointRouteParams
						>;
						entryPointParams: EntryPointRouteParams;
						options: {
							fetchPolicy: 'store-or-network';
						};
				  }
				| undefined;
		};
	} => ({
		entryPoints: {
			...(fg('jpx-463-spa-relay-query')
				? {
						[SPA_CONTENT]: {
							entryPoint: spaEntryPoint,
							entryPointParams,
							options: {
								fetchPolicy: 'store-or-network' as const,
							},
						},
					}
				: undefined),
			[MAIN_CONTENT]: {
				entryPoint: page.main,
				entryPointParams,
			},
			[TOP_NAVIGATION]: {
				entryPoint: topNavEntryPoint,
				entryPointParams,
			},
			...(page.leftSidebar
				? {
						[LEFT_SIDEBAR]: {
							entryPoint: page.leftSidebar.sidebar,
							entryPointParams: {
								...entryPointParams,
								entryPoints: {
									...(page.leftSidebar.entryPoints
										? injectEntryPointParams(page.leftSidebar.entryPoints, entryPointParams)
										: undefined),
								},
								isAppsExpanded: page.leftSidebar.isAppsExpanded ?? false,
								isFiltersExpanded: page.leftSidebar.isFiltersExpanded ?? false,
								isPlansExpanded: page.leftSidebar.isPlansExpanded ?? false,
								isProjectsExpanded: page.leftSidebar.isProjectsExpanded ?? false,
								isOperationsExpanded: page.leftSidebar.isOperationsExpanded ?? false,
							},
						},
					}
				: undefined),
			...(page.previewPanel && fg('nin_glance_panel_technical_changes')
				? {
						[PREVIEW_PANEL]: {
							entryPoint: page.previewPanel,
							entryPointParams,
						},
					}
				: undefined),
			...(page.horizontalNavigation
				? {
						[HORIZONTAL_NAVIGATION]: {
							entryPoint: page.horizontalNavigation,
							entryPointParams: {
								...entryPointParams,
								fallback: page.horizontalNavigationFallback,
							},
						},
					}
				: undefined),
			...(page.rightSidebar
				? {
						[RIGHT_SIDEBAR]: {
							entryPoint: page.rightSidebar,
							entryPointParams: { ...entryPointParams, fallback: page.rightSidebarFallback },
						},
					}
				: undefined),
			...(page.redirect
				? {
						[REDIRECT]: {
							entryPoint: page.redirect,
							entryPointParams,
						},
					}
				: undefined),
		},
	}),
	toString: () => 'PageEntryPoint',
});

function injectEntryPointParams(
	obj: Record<string, { entryPoint: AnyEntryPoint }>,
	entryPointParams: EntryPointRouteParams,
): Record<string, { entryPoint: AnyEntryPoint; entryPointParams: EntryPointRouteParams }> {
	const newParams: Record<
		string,
		{ entryPoint: AnyEntryPoint; entryPointParams: EntryPointRouteParams }
	> = {};

	// https://stackoverflow.com/a/16735184
	for (const prop in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, prop)) {
			newParams[prop] = { entryPoint: obj[prop].entryPoint, entryPointParams };
		}
	}
	return newParams;
}
